import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Trophy, Plus, Trash2, User, Star } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminSnakeLeaderboard() {
  const navigate = useNavigate();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [newScore, setNewScore] = useState("");

  const getAuth = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  };

  useEffect(() => {
    fetchScores();
  }, []);

  const fetchScores = async () => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.get(`${API}/admin/snake/leaderboard`, { headers });
      setScores(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des scores");
    } finally {
      setLoading(false);
    }
  };

  const handleAddFakeScore = async (e) => {
    e.preventDefault();
    const headers = getAuth();
    if (!headers) return;

    if (!newName.trim() || !newScore) {
      toast.error("Veuillez remplir le nom et le score");
      return;
    }

    try {
      await axios.post(
        `${API}/admin/snake/fake-score`,
        { name: newName.trim(), score: parseInt(newScore) },
        { headers }
      );
      toast.success("Score fictif ajouté !");
      setNewName("");
      setNewScore("");
      fetchScores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleDeleteScore = async (scoreId) => {
    const headers = getAuth();
    if (!headers) return;

    if (!window.confirm("Supprimer ce score ?")) return;

    try {
      await axios.delete(`${API}/admin/snake/score/${scoreId}`, { headers });
      toast.success("Score supprimé !");
      fetchScores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/admin/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-pink-800 flex items-center gap-2">
              <Trophy className="w-6 h-6" />
              Podium Snake Sakura
            </h1>
            <p className="text-slate-500 text-sm">Gérer les scores du classement</p>
          </div>
        </div>

        {/* Add Fake Score Form */}
        <Card className="mb-6 border-pink-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Plus className="w-5 h-5 text-pink-600" />
              Ajouter un score fictif
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddFakeScore} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="name" className="text-sm text-slate-600">Nom du joueur</Label>
                <Input
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Ex: Marie L."
                  className="mt-1"
                />
              </div>
              <div className="w-full sm:w-32">
                <Label htmlFor="score" className="text-sm text-slate-600">Score</Label>
                <Input
                  id="score"
                  type="number"
                  value={newScore}
                  onChange={(e) => setNewScore(e.target.value)}
                  placeholder="Ex: 25"
                  className="mt-1"
                />
              </div>
              <div className="flex items-end">
                <Button type="submit" className="bg-pink-600 hover:bg-pink-700 w-full sm:w-auto">
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Current Leaderboard */}
        <Card className="border-pink-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Classement actuel
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500 py-4">Chargement...</p>
            ) : scores.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Aucun score enregistré</p>
            ) : (
              <div className="space-y-2">
                {scores.map((score, index) => (
                  <div
                    key={score.id || score.customer_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      index === 0 ? "bg-yellow-50 border border-yellow-200" :
                      index === 1 ? "bg-slate-100 border border-slate-200" :
                      index === 2 ? "bg-amber-50 border border-amber-200" :
                      "bg-white border border-slate-100"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0 ? "bg-yellow-400 text-yellow-900" :
                        index === 1 ? "bg-slate-300 text-slate-700" :
                        index === 2 ? "bg-amber-400 text-amber-900" :
                        "bg-pink-100 text-pink-600"
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          {score.first_name} {score.last_name}
                          {score.is_fake && (
                            <span className="text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                              Fictif
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(score.created_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg text-pink-600">{score.score} pts</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteScore(score.id || score.customer_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-center text-sm text-slate-500 mt-4">
          💡 Seuls les 3 premiers scores apparaissent sur le podium public
        </p>
      </div>
    </div>
  );
}
